import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { groupCalendarsByRegion, formatHolidayCount, startOfDay } from "common/holiday-calendar";
import AppButton from "components/app-button";
import AppSelectInput from "components/app-select-input";
import AppImportIcon from "components/icons/app-import-icon";

const SOURCES = { CALENDAR: "CALENDAR", PREVIOUS_YEAR: "PREVIOUS_YEAR" };

//prettier-ignore
const sourceOptions = [
	{ label: "Existing Holiday Calendar", value: SOURCES.CALENDAR },
	{ label: "Previous Year", value: SOURCES.PREVIOUS_YEAR },
];

/* A holiday has no id of its own, so title + start date is the only thing that identifies
   one. Imports match on that signature to avoid stacking duplicates when run twice. */
const signatureOf = (holiday) => (holiday.title || "").trim().toLowerCase() + "|" + (startOfDay(holiday.startDate)?.getTime() || "");

export const AppImportHolidaysModal = (props, ref) => {
	const dispatch = useDispatch();
	const [visible, setVisible] = useState(false);
	const [target, setTarget] = useState();
	const calendar = useMemo(() => target?.calendar, [target]);
	const record = useMemo(() => target?.record, [target]);
	const calendars = useMemo(() => groupCalendarsByRegion(target?.records), [target]);
	//prettier-ignore
	const otherCalendars = useMemo(() => calendars.filter((o) => o.regionCode !== calendar?.regionCode), [calendars, calendar]);
	//prettier-ignore
	const calendarOptions = useMemo(() => otherCalendars.map((o) => ({ label: o.title + " - " + (o.region || o.regionCode), value: o.regionCode })), [otherCalendars]);
	//prettier-ignore
	const previousYearOptions = useMemo(() => (calendar?.years || []).filter((o) => o.id !== record?.id && o.holidays?.length).map((o) => ({ label: o.year.toString() + " (" + formatHolidayCount(o.holidays?.length || 0) + ")", value: o.id.toString() })), [calendar, record]);
	//prettier-ignore
	const initialValues = useMemo(() => ({ source: "", regionCode: "", sourceId: "" }), []);
	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: yup.object({
			source: yup.string().required(ERRORS.REQUIRED),
			regionCode: yup.string().when("source", {
				is: SOURCES.CALENDAR,
				then: (schema) => schema.required(ERRORS.REQUIRED),
			}),
			sourceId: yup.string().when("source", {
				is: SOURCES.PREVIOUS_YEAR,
				then: (schema) => schema.required(ERRORS.REQUIRED),
			}),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	/* Another calendar holds one record per year, so the year matching the one being edited
	   is preferred and its latest year is the fallback. Which year that resolves to is spelt
	   out in the banner rather than left implicit. */
	//prettier-ignore
	const sourceRecord = useMemo(() => {
		if (formik.values.source === SOURCES.PREVIOUS_YEAR) {
			return (calendar?.years || []).find((o) => o.id.toString() === formik.values.sourceId);
		}

		if (formik.values.source === SOURCES.CALENDAR) {
			const selected = otherCalendars.find((o) => o.regionCode === formik.values.regionCode);

			if (!selected) return null;

			return selected.years.find((o) => o.year === record?.year) || selected.years[0];
		}

		return null;
	}, [formik.values.source, formik.values.sourceId, formik.values.regionCode, calendar, otherCalendars, record]);

	//prettier-ignore
	const importable = useMemo(() => {
		if (!sourceRecord) return [];

		const existing = new Set((record?.holidays || []).map(signatureOf));

		return (sourceRecord.holidays || []).filter((o) => !existing.has(signatureOf(o)));
	}, [sourceRecord, record]);

	const isValid = useMemo(() => !!sourceRecord && !!importable.length, [sourceRecord, importable]);

	const onHandleShow = useCallback((data) => {
		setTarget(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setTarget();
		setVisible(false);
	}, [formik]);

	//prettier-ignore
	const onHandleChangeSource = useCallback((event) => {
		formik.setFieldValue("source", event.target.value);
		formik.setFieldValue("regionCode", "");
		formik.setFieldValue("sourceId", "");
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async () => {
		let response = null;

		try {
			const holidays = [...(record.holidays || []), ...importable].map((o) => ({ title: o.title, startDate: o.startDate, endDate: o.endDate || null }));

			await api.post.holidayCalendar.update({ id: record.id, title: record.title, region: record.region, regionCode: record.regionCode, year: record.year, holidays });

			response = importable.length;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: formatHolidayCount(response) + " have been imported into " + record.year }));
			props.onHandleGetList();
		}
	}, [dispatch, onHandleDismiss, record, importable, formik, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	const Banner = useCallback(() => {
		if (!formik.values.source) return null;

		if (!sourceRecord) {
			if (formik.values.source === SOURCES.CALENDAR && !calendarOptions.length) return <p className="import-holidays-modal__banner">There are no other holiday calendars to import from yet.</p>;

			if (formik.values.source === SOURCES.PREVIOUS_YEAR && !previousYearOptions.length) return <p className="import-holidays-modal__banner">This calendar has no other year to import from yet.</p>;

			return null;
		}

		if (!importable.length) return <p className="import-holidays-modal__banner">Every holiday in {sourceRecord.year} is already in {record?.year}. There is nothing left to import.</p>;

		return (
			<p className="import-holidays-modal__banner">
				{formatHolidayCount(importable.length)} from <b>{sourceRecord.title}</b> ({sourceRecord.year}) will be added to {record?.year}. Existing holidays are kept, duplicates are skipped, and dates are copied as they are - review any holiday that moves from year to year.
			</p>
		);
	}, [formik.values.source, sourceRecord, importable, record, calendarOptions, previousYearOptions]);

	return (
		<Modal classes={{ root: "app-import-holidays-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="import-holidays-modal" aria-describedby="import-holidays-modal">
			<div className="import-holidays-modal">
				<div className="import-holidays-modal__main">
					<div className="import-holidays-modal__header">
						<p className="import-holidays-modal__title">Import Holidays</p>
						<p className="import-holidays-modal__description">Copy holidays into {record?.year} without replacing what is already there.</p>
					</div>

					<form className="import-holidays-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppSelectInput required searchable={false} name="source" label="Import Source" placeholder="Select a source" options={sourceOptions} value={formik.values.source} error={formik.errors.source} touched={formik.touched.source} disabled={formik.isSubmitting} onChange={onHandleChangeSource} />

						{/* prettier-ignore */}
						{formik.values.source === SOURCES.CALENDAR && !!calendarOptions.length && <AppSelectInput required searchable={false} name="regionCode" label="Holiday Calendar" placeholder="Select a calendar" options={calendarOptions} value={formik.values.regionCode} error={formik.errors.regionCode} touched={formik.touched.regionCode} disabled={formik.isSubmitting} onChange={formik.handleChange} />}

						{/* prettier-ignore */}
						{formik.values.source === SOURCES.PREVIOUS_YEAR && !!previousYearOptions.length && <AppSelectInput required searchable={false} name="sourceId" label="Year" placeholder="Select a year" options={previousYearOptions} value={formik.values.sourceId} error={formik.errors.sourceId} touched={formik.touched.sourceId} disabled={formik.isSubmitting} onChange={formik.handleChange} />}

						<Banner />

						<div className="import-holidays-modal__button-container">
							<AppButton type="button" label="Cancel" outline disabled={formik.isSubmitting} onClick={onHandleDismiss} />
							<AppButton type="submit" className="import-holidays-modal__submit" label="Import" icon={<AppImportIcon color="#ffffff" />} disabled={formik.isSubmitting || !isValid} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppImportHolidaysModal));

AppImportHolidaysModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func.isRequired,
};
